export default class GoogleReviews extends HTMLElement {
    constructor() {
        super();

        this.reviews = [];
        this.currentIndex = 0;
        this.autoplayInterval = null;
        
        // Bind methods to preserve context
        this.goToPrev = this.prevReview.bind(this);
        this.goToNext = this.nextReview.bind(this);
        this.stopAutoplayBound = this.stopAutoplay.bind(this);
        this.startAutoplayBound = this.startAutoplay.bind(this);
        
        // Add CSS animations for transitions
        this.addAnimationStyles();

        const reviewsAttr = this.getAttribute("reviews");
        if (reviewsAttr) {
            try {
                this.reviews = JSON.parse(reviewsAttr);
            } catch (e) {
                console.error("Failed to parse reviews attribute:", e);
            }
        }
    }

    connectedCallback() {
        this.render();
        this.setupEventListeners();
        this.startAutoplay();
    }

    disconnectedCallback() {
        this.stopAutoplay();
        this.removeEventListeners();
        
        // Remove animation styles when component is disconnected
        this.removeAnimationStyles();
    }

    render() {
        this.innerHTML = "";
        
        // Main container
        const container = document.createElement("div");
        container.className = "relative mx-auto overflow-hidden flex justify-center items-center";
        container.style.height = "350px"; // Reduced height for more compact layout
        container.style.width = "100%"; // Full width container
        
        // Carousel container with 3D perspective
        const carouselContainer = document.createElement("div");
        carouselContainer.className = "carousel-container relative w-full h-full flex justify-center items-center";
        carouselContainer.style.perspective = "1000px";
        carouselContainer.style.transformStyle = "preserve-3d";
        carouselContainer.style.maxWidth = "1200px"; // Add max width for better centering
        
        // Create carousel
        const carousel = document.createElement("div");
        carousel.className = "carousel relative w-full h-full flex justify-center items-center";
        carousel.style.transformStyle = "preserve-3d";
        carousel.style.transition = "transform 0.5s ease-in-out";
        this.carousel = carousel; // Store reference for animations
        
        // Add reviews as cards
        this.reviews.forEach((review, index) => {
            const card = document.createElement("div");
            card.className = "review-card absolute top-0 left-0 right-0 mx-auto cursor-pointer";
            // Width will be set dynamically based on position
            card.style.height = "220px"; // Reduced height to match the second image
            card.style.transition = "all 0.5s ease-in-out";
            card.style.transformStyle = "preserve-3d";
            card.style.backfaceVisibility = "hidden";
            card.dataset.index = index;
            
            // Position cards in 3D space
            let translateZ, translateX, translateY;
            let cardWidth;
            let rotateY = 0;
            
            if (index === this.currentIndex) {
                // Active card - front and center
                translateZ = 0;
                translateX = 0;
                translateY = 0;
                cardWidth = "min(90%, 38rem)";
            } else {
                // Inactive cards - stacked behind with more noticeable upward position
                const offset = Math.abs(index - this.currentIndex);
                translateZ = -50 - (offset * 30); // Stack cards behind with reduced depth
                
                // Keep all cards centered horizontally
                translateX = 0;
                
                // Apply slight rotation based on position in stack
                if (index > this.currentIndex) {
                    rotateY = -5; // Slight angle for cards in back
                } else {
                    rotateY = 5; // Slight angle for cards in back
                }
                
                translateY = -10 - (offset * 10); // More noticeable upward position
                cardWidth = "min(90%, 38rem)";
            }
            
            card.style.width = cardWidth;
            card.style.transform = `translateX(${translateX}px) translateY(${translateY}px) translateZ(${translateZ}px) rotateY(${rotateY}deg)`;
            card.style.zIndex = index === this.currentIndex ? 10 : 5 - Math.abs(index - this.currentIndex);
            card.style.opacity = index === this.currentIndex ? 1 : 0.7 - (0.1 * Math.abs(index - this.currentIndex));
            
            // Card content styled to match the second image
            card.innerHTML = `
                <div class="w-full h-full border-gray-300 border rounded-3xl relative flex flex-col items-center justify-center bg-white shadow-md p-4">
                    <span class="text-4xl font-bold absolute top-3 left-5 text-gray-800">"</span>
                    <p class="text-center font-medium text-base px-8 mt-2">${review.text}</p>
                    <div class="flex justify-center my-2">
                        ${Array(review.rating || 5).fill('<img src="/wp-content/themes/badaxe/img/homepage-ab/star.svg" alt="star" class="h-4 w-4 mx-0.5" />').join('')}
                    </div>
                    <h3 class="text-center text-sm font-semibold text-gray-700">${review.name}, ${review.location}</h3>
                    <span class="text-4xl font-bold absolute bottom-3 right-5 text-gray-800">"</span>
                    ${index === this.currentIndex ? `
                    <button class="prev-button absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full z-30" aria-label="Previous review">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                    </button>
                    <button class="next-button absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full z-30" aria-label="Next review">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                    </button>
                    <div class="indicators absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-30">
                        ${this.reviews.map((_, i) => `<button class="indicator-dot w-3 h-3 rounded-full ${i === this.currentIndex ? 'bg-black' : 'bg-gray-300'}" data-index="${i}" aria-label="Go to review ${i + 1}"></button>`).join('')}
                    </div>
                    ` : ''}
                </div>
            `;
            
            carousel.appendChild(card);
        });
        
        // Navigation controls are now embedded in the active card
        
        // Indicators are now embedded in the active card
        
        // Assemble the carousel
        carouselContainer.appendChild(carousel);
        container.appendChild(carouselContainer);
        
        this.appendChild(container);
        
        // Store references
        this.carousel = carousel;
    }
    
    setupEventListeners() {
        // Add event listeners to the navigation buttons embedded in the active card
        const prevButtons = this.querySelectorAll('.prev-button');
        const nextButtons = this.querySelectorAll('.next-button');
        
        prevButtons.forEach(button => {
            button.addEventListener('click', this.goToPrev);
        });
        
        nextButtons.forEach(button => {
            button.addEventListener('click', this.goToNext);
        });
        
        // Add event listeners to indicator dots
        const dots = this.querySelectorAll('.indicator-dot');
        dots.forEach(dot => {
            dot.addEventListener('click', () => {
                const index = parseInt(dot.dataset.index);
                this.goToReview(index);
            });
        });
        
        // Add click event to cards
        const cards = this.querySelectorAll('.review-card');
        cards.forEach(card => {
            card.addEventListener('click', () => {
                const index = parseInt(card.dataset.index);
                if (index !== this.currentIndex) {
                    this.goToReview(index);
                }
            });
        });
        
        // Pause autoplay on hover
        this.addEventListener('mouseenter', this.stopAutoplayBound);
        this.addEventListener('mouseleave', this.startAutoplayBound);
    }
    
    removeEventListeners() {
        // Remove event listeners from the navigation buttons
        const prevButtons = this.querySelectorAll('.prev-button');
        const nextButtons = this.querySelectorAll('.next-button');
        
        prevButtons.forEach(button => {
            button.removeEventListener('click', this.goToPrev);
        });
        
        nextButtons.forEach(button => {
            button.removeEventListener('click', this.goToNext);
        });
        
        this.removeEventListener('mouseenter', this.stopAutoplayBound);
        this.removeEventListener('mouseleave', this.startAutoplayBound);
    }
    
    nextReview() {
        // Add a class to the carousel for animation direction
        if (this.carousel) {
            this.carousel.classList.add('moving-right-to-left');
        }
        
        // After a short delay, update the index and carousel
        setTimeout(() => {
            this.currentIndex = (this.currentIndex + 1) % this.reviews.length;
            this.updateCarousel();
            
            // Remove the animation class after transition completes
            setTimeout(() => {
                if (this.carousel) {
                    this.carousel.classList.remove('moving-right-to-left');
                }
            }, 500);
        }, 50);
    }
    
    prevReview() {
        // Add a class to the carousel for animation direction
        if (this.carousel) {
            this.carousel.classList.add('moving-left-to-right');
        }
        
        // After a short delay, update the index and carousel
        setTimeout(() => {
            this.currentIndex = (this.currentIndex - 1 + this.reviews.length) % this.reviews.length;
            this.updateCarousel();
            
            // Remove the animation class after transition completes
            setTimeout(() => {
                if (this.carousel) {
                    this.carousel.classList.remove('moving-left-to-right');
                }
            }, 500);
        }, 50);
    }
    
    goToReview(index) {
        if (index >= 0 && index < this.reviews.length) {
            // Determine animation direction based on index change
            if (index > this.currentIndex) {
                this.carousel.classList.add('moving-right-to-left');
            } else if (index < this.currentIndex) {
                this.carousel.classList.add('moving-left-to-right');
            }
            
            // After a short delay, update the index and carousel
            setTimeout(() => {
                this.currentIndex = index;
                this.updateCarousel();
                
                // Remove the animation classes after transition completes
                setTimeout(() => {
                    this.carousel.classList.remove('moving-right-to-left', 'moving-left-to-right');
                }, 500);
            }, 50);
        }
    }
    
    updateCarousel() {
        const cards = this.querySelectorAll('.review-card');
        
        cards.forEach((card, index) => {
            let translateZ, translateX, translateY;
            let cardWidth;
            let rotateY = 0;
            
            if (index === this.currentIndex) {
                translateZ = 0;
                translateX = 0;
                translateY = 0;
                cardWidth = "min(90%, 38rem)";
                
                // Make sure the active card has navigation elements
                const cardContent = card.querySelector('div');
                
                // Check if navigation buttons already exist
                const hasNavButtons = card.querySelector('.prev-button') !== null;
                
                if (!hasNavButtons && cardContent) {
                    // Add navigation buttons to the active card (smaller size)
                    const prevButton = document.createElement('button');
                    prevButton.className = "prev-button absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full z-30";
                    prevButton.setAttribute("aria-label", "Previous review");
                    prevButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 18l-6-6 6-6"/></svg>`;
                    prevButton.addEventListener('click', this.goToPrev);
                    
                    const nextButton = document.createElement('button');
                    nextButton.className = "next-button absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full z-30";
                    nextButton.setAttribute("aria-label", "Next review");
                    nextButton.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>`;
                    nextButton.addEventListener('click', this.goToNext);
                    
                    // Add indicator dots
                    const indicators = document.createElement('div');
                    indicators.className = "indicators absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-30";
                    
                    this.reviews.forEach((_, i) => {
                        const dot = document.createElement('button');
                        dot.className = `indicator-dot w-3 h-3 rounded-full ${i === this.currentIndex ? 'bg-black' : 'bg-gray-300'}`;
                        dot.dataset.index = i;
                        dot.setAttribute("aria-label", `Go to review ${i + 1}`);
                        dot.addEventListener('click', () => this.goToReview(i));
                        indicators.appendChild(dot);
                    });
                    
                    cardContent.appendChild(prevButton);
                    cardContent.appendChild(nextButton);
                    cardContent.appendChild(indicators);
                }
            } else {
                const offset = Math.abs(index - this.currentIndex);
                translateZ = -50 - (offset * 30);
                
                // Keep all cards centered horizontally
                translateX = 0;
                
                // Apply slight rotation based on position in stack
                if (index > this.currentIndex) {
                    rotateY = -5;
                } else {
                    rotateY = 5;
                }
                
                translateY = -10 - (offset * 10); // Upward position for stacked effect
                cardWidth = "min(90%, 38rem)";
                
                // Remove navigation elements from inactive cards
                const prevButton = card.querySelector('.prev-button');
                const nextButton = card.querySelector('.next-button');
                const indicators = card.querySelector('.indicators');
                
                if (prevButton) prevButton.remove();
                if (nextButton) nextButton.remove();
                if (indicators) indicators.remove();
            }
            
            // Set CSS variable for offset to use in animations
            card.style.setProperty('--offset', Math.abs(index - this.currentIndex));
            
            card.style.width = cardWidth;
            card.style.transform = `translateX(${translateX}px) translateY(${translateY}px) translateZ(${translateZ}px) rotateY(${rotateY}deg)`;
            card.style.zIndex = index === this.currentIndex ? 10 : 5 - Math.abs(index - this.currentIndex);
            card.style.opacity = index === this.currentIndex ? 1 : 0.7 - (0.1 * Math.abs(index - this.currentIndex));
        });
        
        // Update indicator dots if they exist
        const dots = this.querySelectorAll('.indicator-dot');
        dots.forEach((dot, index) => {
            dot.className = `indicator-dot w-3 h-3 rounded-full ${index === this.currentIndex ? 'bg-black' : 'bg-gray-300'}`;
        });
        
        // Reset autoplay timer
        this.stopAutoplay();
        this.startAutoplay();
    }
    
    startAutoplay() {
        if (this.autoplayInterval) return;
        
        this.autoplayInterval = setInterval(() => {
            this.nextReview();
        }, 5000); // Change slide every 5 seconds
    }
    
    stopAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval);
            this.autoplayInterval = null;
        }
    }
    
    // Add CSS animations for card transitions
    addAnimationStyles() {
        // Remove existing style element if it exists
        this.removeAnimationStyles();
        
        // Create style element
        const style = document.createElement('style');
        style.id = 'google-reviews-animations';
        
        // Define animations
        style.textContent = `
            /* Right to left animation - for cards coming to the front */
            .carousel.moving-right-to-left .review-card {
                animation: slideRightToLeft 0.5s ease-in-out forwards;
            }
            
            @keyframes slideRightToLeft {
                0% {
                    transform: translateX(0) 
                              translateY(calc(-10px - var(--offset, 0) * 10px)) 
                              translateZ(calc(-50px - var(--offset, 0) * 30px)) 
                              rotateY(-5deg);
                }
                100% {
                    transform: translateX(0) translateY(0) translateZ(0) rotateY(0deg);
                }
            }
            
            /* Left to right animation - for cards going to the back */
            .carousel.moving-left-to-right .review-card {
                animation: slideLeftToRight 0.5s ease-in-out forwards;
            }
            
            @keyframes slideLeftToRight {
                0% {
                    transform: translateX(0) 
                              translateY(calc(-10px - var(--offset, 0) * 10px)) 
                              translateZ(calc(-50px - var(--offset, 0) * 30px)) 
                              rotateY(5deg);
                }
                100% {
                    transform: translateX(0) translateY(0) translateZ(0) rotateY(0deg);
                }
            }
        `;
        
        // Add style to document head
        document.head.appendChild(style);
        this.animationStyle = style;
    }
    
    // Remove animation styles
    removeAnimationStyles() {
        if (this.animationStyle && document.head.contains(this.animationStyle)) {
            document.head.removeChild(this.animationStyle);
        }
    }
}
